import gql from "graphql-tag";
import { App } from "./app-config.js";
import { IImagePreview, Image, IMediaItem } from "./gallery.js";
import {
    Copyright,
    EngagementInfo,
    FeedConnection,
    TextBannerItem,
    QA,
} from "./_common.js";
import {
    image,
    imagePreview,
    infographicsBannerInfo,
    mediaItem,
    quizBannerInfo,
} from "./fragments.js";
import type { LangCode } from "../../../_common/types.js";
import { Topic } from "./topics.js";

export const newsPreview = gql`
    fragment newsPreview on News {
        id
        date
        content(langCode: $langCode) {
            title
            preview
            readingTime
        }
        image {
            ...imagePreview
        }
    }
    ${imagePreview}
`;

export interface News<C, I, M = undefined, A = undefined, F = undefined> {
    __typename: "News";
    id: string;
    slug: string | null;
    date: string;
    updatedAt: string;
    featured: boolean;
    contentLangs: LangCode[];
    content: C;
    image: I;
    textImages: (I | null)[] | null;
    textBannersItems: (TextBannerItem | null)[] | null;
    mediaItem: M | null;
    promoApps: A[];
    copyright: Copyright;
    featuredNews: F[] | null;
    engagementInfo: EngagementInfo;
    customBanners: NewsCustomBanner[] | null;
    tags: string[] | null;
    topics: (Topic | null)[];
}

export interface NewsCustomBanner {
    id: string;
    imgUrl: string;
    alt: string;
    aspectRatio: string;
    url: string;
    siteAdUnit: string;
}

export interface NewsContent {
    title: string;
    metaTitle: string;
    preview: string;
    text: string;
    readingTime: number | null;
    tocHeader: string | null;
    keywords: string | null;
    faq: QA[] | null;
}

export interface GetNewsVariables {
    langCode?: LangCode;
    id: string;
    featuredFirst: number;
}

export type NewsItem = News<NewsContent, Image, IMediaItem, App, NewsForFeed>;

export interface GetNewsData {
    news: NewsItem | null;
}

export const GET_NEWS = gql`
    query getNews($langCode: LangCode, $id: NewsID!, $featuredFirst: Int) {
        news(id: $id) {
            id
            slug
            date
            updatedAt
            featured
            contentLangs
            content(langCode: $langCode) {
                title
                metaTitle
                preview
                text
                readingTime
                tocHeader
                keywords
                faq {
                    q
                    a
                }
            }
            image {
                ...image
            }
            textImages(langCode: $langCode) {
                ...image
            }
            textBannersItems(langCode: $langCode) {
                id
                __typename
                ...quizBannerInfo
                ...infographicsBannerInfo
            }
            mediaItem {
                ...mediaItem
            }
            promoApps(platforms: [ios, android, huawei]) {
                id
                title
                icon {
                    sourceUrl
                    sizes
                }
                marketUrl
                marketId
                platform
            }
            copyright {
                author
                url
            }
            tags
            customBanners(langCode: $langCode) {
                id
                siteAdUnit
                imgUrl
                alt
                url
                aspectRatio
            }
            featuredNews(first: $featuredFirst) {
                id
                date
                featured
                content(langCode: $langCode) {
                    title
                    preview
                    readingTime
                }
                image {
                    ...imagePreview
                }
            }
            engagementInfo {
                facebook
                messenger
                twitter
                telegram
                whatsapp
                native
            }
            topics {
                id
                content(langCode: $langCode) {
                    title
                }
            }
        }
    }
    ${imagePreview}
    ${image}
    ${mediaItem}
    ${quizBannerInfo}
    ${infographicsBannerInfo}
`;

export interface GetNewsFeedVariables {
    langCode: LangCode;
    first: number;
    after?: string;
}

type NewsContentForFeed = Pick<
    NewsContent,
    "title" | "preview" | "readingTime"
>;

export type NewsForFeed = Pick<
    News<NewsContentForFeed, IImagePreview>,
    "__typename" | "id" | "content" | "date" | "featured" | "image"
>;
export interface GetNewsFeedData {
    newsFeed: FeedConnection<NewsForFeed> | null;
}

export const GET_NEWS_FEED = gql`
    query getNewsFeed($langCode: LangCode, $first: Int, $after: NewsCursor) {
        newsFeed(first: $first, after: $after) {
            totalCount
            offset
            edges {
                cursor
                node {
                    id
                    date
                    featured
                    content(langCode: $langCode) {
                        title
                        preview
                        readingTime
                    }
                    image {
                        ...imagePreview
                    }
                }
            }
        }
    }
    ${imagePreview}
`;
